export type LiveDeliveryMode = 'interactive' | 'balanced' | 'quality';
export type LiveDeliveryLatencyClass = 'ultra_low' | 'low' | 'standard';
export type LiveDeliveryBackend = 'peer_mesh' | 'stage_sfu' | 'low_latency_cdn' | 'encoder_ingest_cdn';
export type LiveDeliveryTransport = 'webrtc' | 'low_latency_hls' | 'hls' | 'none';
export type LiveDeliverySource = 'native' | 'shorts' | 'meeting' | 'event' | 'youtube' | 'battle' | 'audio';

export interface LiveDeliveryContract {
  mode: LiveDeliveryMode;
  viewerTransport: LiveDeliveryTransport;
  stageTransport: LiveDeliveryTransport;
  plannedBackend: LiveDeliveryBackend;
  latencyClass: LiveDeliveryLatencyClass;
  supportsExternalEncoder: boolean;
  supportsDvr: boolean;
  summary: string;
}

export interface ResolveLiveDeliveryInput {
  source: LiveDeliverySource;
  shortsMode?: 'broadcast' | 'interactive';
  broadcastMeeting?: boolean;
  deliveryMode?: LiveDeliveryMode;
  deliveryContract?: LiveDeliveryContract;
}

export const LIVE_DELIVERY_MODE_OPTIONS: Array<{
  mode: LiveDeliveryMode;
  label: string;
  badge: string;
  description: string;
  operatorNote: string;
}> = [
  {
    mode: 'interactive',
    label: 'Interactive',
    badge: 'Lowest latency',
    description: 'Optimized for direct conversation, guest timing, and quick audience feedback.',
    operatorNote: 'Best fit for closed rooms or highly interactive sessions. Viewer scale is the weakest here.',
  },
  {
    mode: 'balanced',
    label: 'Balanced',
    badge: 'Recommended',
    description: 'Keeps stage latency low while preparing public viewing to move to broadcast fanout.',
    operatorNote: 'Best default for production podcasts and public Events.',
  },
  {
    mode: 'quality',
    label: 'Quality',
    badge: 'Encoder ready',
    description: 'Prioritizes stable audience delivery, external encoder support, and future DVR/archive readiness.',
    operatorNote: 'Best fit for planned productions where quality matters more than fastest interaction.',
  },
];

export const resolveLiveDeliveryMode = (input: ResolveLiveDeliveryInput): LiveDeliveryMode => {
  if (input.deliveryContract?.mode) {
    return input.deliveryContract.mode;
  }

  if (input.deliveryMode) {
    return input.deliveryMode;
  }

  if (input.source === 'event' || input.source === 'native') {
    return 'balanced';
  }

  if (input.source === 'meeting') {
    return input.broadcastMeeting ? 'balanced' : 'interactive';
  }

  if (input.source === 'shorts') {
    return input.shortsMode === 'broadcast' ? 'balanced' : 'interactive';
  }

  return 'interactive';
};

export const resolveLiveDeliveryContract = (
  input: ResolveLiveDeliveryInput,
): LiveDeliveryContract => {
  if (input.deliveryContract) {
    return {
      ...input.deliveryContract,
      mode: resolveLiveDeliveryMode(input),
    };
  }

  const mode = resolveLiveDeliveryMode(input);

  if (mode === 'quality') {
    return {
      mode,
      viewerTransport: 'hls',
      stageTransport: 'webrtc',
      plannedBackend: 'encoder_ingest_cdn',
      latencyClass: 'standard',
      supportsExternalEncoder: true,
      supportsDvr: true,
      summary: 'Encoder-first delivery contract for planned productions, stronger audience stability, and archive/DVR readiness.',
    };
  }

  if (mode === 'balanced') {
    return {
      mode,
      viewerTransport: 'low_latency_hls',
      stageTransport: 'webrtc',
      plannedBackend: 'low_latency_cdn',
      latencyClass: 'low',
      supportsExternalEncoder: true,
      supportsDvr: true,
      summary: 'Hybrid delivery contract: low-latency stage path now, public audience fanout path prepared for SFU/CDN rollout.',
    };
  }

  return {
    mode,
    viewerTransport: 'webrtc',
    stageTransport: 'webrtc',
    plannedBackend: 'stage_sfu',
    latencyClass: 'ultra_low',
    supportsExternalEncoder: false,
    supportsDvr: false,
    summary: 'Realtime-first delivery contract for direct interaction, guest timing, and low-latency room behavior.',
  };
};
